import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * CASE FILE: ZERO
 * Campaign landing page for Book 1 (Node Zero) — Rane Dynamics
 * Campaign: LAND-0001 · Experiment: EXP-0001
 *
 * NOTE: replace PURCHASE_URL below with the final retailer URL when ready.
 */
const PURCHASE_URL = "https://www.amazon.com/dp/B0G4HWSK1D";

const BOOK_COVER =
    "https://customer-assets.emergentagent.com/job_rane-dynamics-films/artifacts/71s45106_file_00000000531472308d5bcc0f51861252.png";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const STORAGE_KEY = "cfz_campaign_utms";

/* ---------- helpers ---------- */

function readUTMs() {
    if (typeof window === "undefined") return {};
    // 1. Try current URL first
    const params = new URLSearchParams(window.location.search);
    const fromUrl = {};
    UTM_KEYS.forEach((k) => {
        const v = params.get(k);
        if (v) fromUrl[k] = v;
    });
    // 2. Persist to sessionStorage if we found any
    if (Object.keys(fromUrl).length > 0) {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl));
        } catch {
            /* private mode / disabled storage */
        }
        return fromUrl;
    }
    // 3. Fallback to previously persisted UTMs in this session
    try {
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored) || {};
    } catch {
        /* fail-safe */
    }
    return {};
}

function appendUTMs(baseUrl, utms) {
    if (!utms || Object.keys(utms).length === 0) return baseUrl;
    try {
        const url = new URL(baseUrl);
        Object.entries(utms).forEach(([k, v]) => url.searchParams.set(k, v));
        return url.toString();
    } catch {
        return baseUrl;
    }
}

/**
 * GA4 reserves the `utm_*` parameter names for its own session-scoped
 * traffic-source attribution. Passing them as custom event params gets
 * ignored. Rename them to `cf_utm_*` on custom events so they're queryable
 * as custom dimensions.
 */
function toEventParams(utms) {
    const out = {};
    Object.entries(utms || {}).forEach(([k, v]) => {
        out["cf_" + k] = v;
    });
    return out;
}

function fireGtag(eventName, payload = {}) {
    if (typeof window === "undefined") return;
    if (typeof window.gtag !== "function") return;
    try {
        window.gtag("event", eventName, {
            campaign_id: "LAND-0001",
            experiment_id: "EXP-0001",
            ...payload,
        });
    } catch {
        /* fail-safe */
    }
}

/* ---------- component ---------- */

export default function CaseFileZero() {
    const [caseOpen, setCaseOpen] = useState(false);
    const [now, setNow] = useState("");
    const sectionTwoRef = useRef(null);
    const utms = useMemo(readUTMs, []);
    const purchaseHref = useMemo(() => appendUTMs(PURCHASE_URL, utms), [utms]);

    // Live "REC" clock — cosmetic classified-terminal detail
    useEffect(() => {
        const tick = () => {
            const d = new Date();
            const utc = d.toISOString().slice(0, 19).replace("T", " ") + "Z";
            setNow(utc);
        };
        tick();
        const iv = setInterval(tick, 1000);
        return () => clearInterval(iv);
    }, []);

    // Fire land_0001_view after 500ms + only when tab is visible (crude bot filter)
    useEffect(() => {
        if (typeof document === "undefined") return;
        if (navigator.webdriver === true) return; // headless / automation
        const t = setTimeout(() => {
            if (document.visibilityState === "visible") {
                fireGtag("land_0001_view", {
                    page_path: "/case-file/zero",
                    ...toEventParams(utms),
                });
            }
        }, 500);
        return () => clearTimeout(t);
    }, [utms]);

    // Reflect campaign params in document title for cleaner GA reports
    useEffect(() => {
        document.title = "CASE FILE: ZERO — Rane Dynamics";
    }, []);

    const handleOpenCase = useCallback(() => {
        setCaseOpen(true);
        fireGtag("case_file_open", toEventParams(utms));
        // Smooth scroll to section 2
        if (sectionTwoRef.current) {
            sectionTwoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [utms]);

    const handlePurchaseClick = useCallback(() => {
        fireGtag("book_purchase_click", {
            book: "SPECTRA // BLACKLINE — NODE ZERO",
            price_display: "$1",
            ...toEventParams(utms),
        });
    }, [utms]);

    const handleRaneClick = useCallback(() => {
        fireGtag("rane_dynamics_click", toEventParams(utms));
    }, [utms]);

    return (
        <div
            data-testid="case-file-zero"
            className="min-h-screen bg-[#050505] text-zinc-200 antialiased"
            style={{
                fontFamily: "'IBM Plex Mono', 'JetBrains Mono', ui-monospace, monospace",
            }}
        >
            {/* Global font import for this page only */}
            <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@300;400;500;600&family=Outfit:wght@300;600;800;900&display=swap"
            />

            {/* Scanlines / grain via inline CSS (very subtle) */}
            <style>{`
                .cfz-grain::before {
                    content: "";
                    position: fixed;
                    inset: 0;
                    pointer-events: none;
                    background-image: repeating-linear-gradient(
                        180deg,
                        rgba(255,255,255,0.015) 0,
                        rgba(255,255,255,0.015) 1px,
                        transparent 1px,
                        transparent 3px
                    );
                    z-index: 1;
                    mix-blend-mode: overlay;
                }
                .cfz-hairline {
                    background-image: linear-gradient(to right, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.15) 100%);
                    background-size: 100% 1px;
                    background-position: 0 100%;
                    background-repeat: no-repeat;
                }
                @keyframes cfz-blink { 50% { opacity: 0.2; } }
                .cfz-blink { animation: cfz-blink 1.4s steps(2, end) infinite; }
                @keyframes cfz-reveal {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .cfz-reveal { animation: cfz-reveal 0.9s ease-out both; }
                .cfz-display { font-family: 'Outfit', sans-serif; letter-spacing: -0.02em; }
            `}</style>

            <div className="cfz-grain relative">
                {/* ================== TERMINAL HEADER STRIP ================== */}
                <header
                    data-testid="cfz-header"
                    className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10"
                >
                    <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-11 flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                        <div className="flex items-center gap-4">
                            <span className="inline-flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] cfz-blink" />
                                REC
                            </span>
                            <span className="hidden md:inline">{now || "0000-00-00 00:00:00Z"}</span>
                            <span className="hidden md:inline text-zinc-600">·</span>
                            <span className="hidden md:inline">NZ-001</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-zinc-500">SPECTRA // BLACKLINE</span>
                            <span className="hidden md:inline text-zinc-600">·</span>
                            <span className="hidden md:inline">INCIDENT ARCHIVE</span>
                        </div>
                    </div>
                </header>

                {/* ================== SECTION 1 — ARCHIVE ENTRY ================== */}
                <section
                    data-testid="cfz-section-archive"
                    className="relative min-h-[calc(100vh-2.75rem)] flex items-center py-16 md:py-24 overflow-hidden"
                >
                    {/* subtle red vignette */}
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-[#DC2626] opacity-[0.06] blur-[160px]" />
                    </div>

                    <div className="relative z-10 w-full max-w-[1200px] mx-auto px-5 md:px-10">
                        {/* Classification header */}
                        <div className="cfz-reveal">
                            <div className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-zinc-500 mb-2">
                                SPECTRA // BLACKLINE
                            </div>
                            <div className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-zinc-500">
                                INCIDENT ARCHIVE // NZ-001
                            </div>

                            <div className="mt-6 inline-flex items-center gap-3 border border-white/15 bg-black/50 px-4 py-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626]" />
                                <span className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-zinc-300">
                                    ACCESS LEVEL:{" "}
                                    <span className="text-white">PUBLIC FRAGMENT</span>
                                </span>
                            </div>
                        </div>

                        {/* Evidence block */}
                        <div
                            className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 cfz-reveal"
                            style={{ animationDelay: "0.15s" }}
                        >
                            <div className="md:col-span-5">
                                <div className="border-l border-white/15 pl-5 md:pl-6 space-y-6">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                                            OFFICIAL CAUSE
                                        </div>
                                        <div className="text-base md:text-lg uppercase tracking-[0.15em] text-zinc-500 line-through decoration-[#DC2626] decoration-2">
                                            REACTOR FAILURE
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                                            RECORD STATUS
                                        </div>
                                        <div className="text-base md:text-lg uppercase tracking-[0.15em] text-[#DC2626]">
                                            COMPROMISED
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="md:col-span-7">
                                <h1 className="cfz-display font-black text-[10vw] md:text-[5.6vw] leading-[0.95] tracking-tight text-white">
                                    THEY BLAMED<br />
                                    THE MACHINE.<br />
                                    <span className="text-[#DC2626]">THEY WERE WRONG.</span>
                                </h1>

                                <div className="mt-10 max-w-xl space-y-3 text-sm md:text-[15px] leading-relaxed text-zinc-400">
                                    <p>The official record says it was an accident.</p>
                                    <p>The evidence says otherwise.</p>
                                    <p>Someone buried what happened at Node Zero.</p>
                                    <p className="text-zinc-200">
                                        There is only one way to find out why.
                                    </p>
                                </div>

                                {/* Primary CTA */}
                                <div className="mt-12">
                                    <button
                                        onClick={handleOpenCase}
                                        data-testid="cfz-open-case-btn"
                                        aria-label="Open case file"
                                        className="group inline-flex items-center gap-3 border border-white/25 hover:border-[#DC2626] bg-transparent hover:bg-[#DC2626] px-7 py-4 transition-colors duration-300"
                                    >
                                        <span className="w-1.5 h-1.5 bg-[#DC2626] group-hover:bg-white transition-colors" />
                                        <span className="text-[11px] md:text-xs uppercase tracking-[0.35em] text-white">
                                            ACCESS GRANTED
                                        </span>
                                        <span className="text-zinc-500 group-hover:text-white/80 text-[10px] tracking-widest transition-colors">
                                            →
                                        </span>
                                    </button>
                                    <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                                        CLEARANCE REQUIRED · DIGITAL FRAGMENT
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bottom corner marks */}
                        <div className="mt-24 md:mt-32 flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                            <span>LAND-0001</span>
                            <span className="hidden md:inline">
                                DECLASSIFIED FRAGMENT · REDACTED SOURCES
                            </span>
                            <span>EXP-0001</span>
                        </div>
                    </div>
                </section>

                {/* ================== SECTION 2 — CASE FILE OPEN ================== */}
                <section
                    ref={sectionTwoRef}
                    data-testid="cfz-section-open"
                    className={`relative bg-[#080808] border-t border-white/10 py-20 md:py-32 transition-opacity duration-700 ${
                        caseOpen ? "opacity-100" : "opacity-100 md:opacity-95"
                    }`}
                >
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#DC2626] opacity-[0.05] blur-[140px]" />
                    </div>

                    <div className="relative z-10 max-w-[1200px] mx-auto px-5 md:px-10">
                        {/* Classification bar */}
                        <div className="flex items-center gap-4 mb-16 md:mb-20 text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#DC2626] cfz-blink" />
                            <span>CASE FILE // ACCESS GRANTED</span>
                            <span className="flex-1 h-px bg-white/10" />
                            <span className="hidden md:inline">NZ-001 · FRAGMENT 01/03</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
                            {/* Book cover */}
                            <div className="md:col-span-5 relative">
                                <div className="relative aspect-[2/3] max-w-[380px] mx-auto md:mx-0 group">
                                    {/* Corner brackets */}
                                    <span className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-white/40" />
                                    <span className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-white/40" />
                                    <span className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-white/40" />
                                    <span className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-white/40" />

                                    <img
                                        src={BOOK_COVER}
                                        alt="Node Zero — Spectra // Blackline Book One by Elias Rane"
                                        data-testid="cfz-book-cover"
                                        className="w-full h-full object-cover shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]"
                                    />

                                    <div className="absolute top-3 left-3 bg-black/70 backdrop-blur px-2 py-1 text-[9px] uppercase tracking-[0.25em] text-white/80 border border-white/15">
                                        EVIDENCE 01
                                    </div>
                                </div>
                            </div>

                            {/* Details */}
                            <div className="md:col-span-7">
                                <div className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-zinc-500">
                                    SPECTRA // BLACKLINE
                                </div>
                                <h2 className="cfz-display mt-4 font-black text-5xl md:text-7xl leading-[0.9] tracking-tight text-white">
                                    NODE ZERO
                                </h2>
                                <div className="mt-4 text-xs uppercase tracking-[0.3em] text-zinc-500">
                                    BOOK ONE · BY ELIAS RANE
                                </div>

                                <div className="mt-10 max-w-md">
                                    <div className="cfz-display text-2xl md:text-3xl font-bold text-zinc-100 tracking-tight">
                                        THE CASE BEGINS HERE.
                                    </div>
                                    <div className="mt-3 text-sm uppercase tracking-[0.25em] text-zinc-500">
                                        DIGITAL EDITION — $1
                                    </div>
                                </div>

                                {/* Purchase CTA */}
                                <a
                                    href={purchaseHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={handlePurchaseClick}
                                    data-testid="cfz-purchase-btn"
                                    className="mt-10 inline-flex items-center gap-3 bg-white text-black hover:bg-[#DC2626] hover:text-white px-8 py-5 transition-colors duration-300"
                                >
                                    <span className="w-1.5 h-1.5 bg-[#DC2626]" />
                                    <span className="text-xs md:text-sm uppercase tracking-[0.3em] font-semibold">
                                        READ NODE ZERO — $1
                                    </span>
                                    <span className="text-[10px] tracking-widest">→</span>
                                </a>

                                {/* Tags */}
                                <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-x-6 gap-y-3 text-[10px] uppercase tracking-[0.35em] text-zinc-400">
                                    <span>SCIENCE</span>
                                    <span className="text-zinc-700">/</span>
                                    <span>INGENUITY</span>
                                    <span className="text-zinc-700">/</span>
                                    <span>CONSEQUENCE</span>
                                    <span className="text-zinc-700">/</span>
                                    <span>REDEMPTION</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ================== SECTION 3 — CLOSING ================== */}
                <section
                    data-testid="cfz-section-close"
                    className="relative bg-black border-t border-white/10 py-24 md:py-36 overflow-hidden"
                >
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-[#DC2626] opacity-[0.05] blur-[160px]" />
                    </div>

                    <div className="relative z-10 max-w-[1200px] mx-auto px-5 md:px-10 text-center">
                        <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-600 mb-8">
                            END OF FRAGMENT
                        </div>

                        <h3 className="cfz-display font-black text-[10vw] md:text-[6.4vw] leading-[0.92] tracking-tight text-white">
                            THIS IS FICTION.
                            <br />
                            <span className="text-[#DC2626]">THE QUESTIONS AREN&apos;T.</span>
                        </h3>

                        <div className="mt-16 md:mt-20">
                            <Link
                                to="/"
                                onClick={handleRaneClick}
                                data-testid="cfz-rane-link"
                                className="inline-flex items-center gap-3 text-zinc-400 hover:text-white transition-colors group"
                            >
                                <span className="w-6 h-px bg-current group-hover:w-10 transition-all duration-300" />
                                <span className="text-xs uppercase tracking-[0.35em]">
                                    EXPLORE RANE DYNAMICS
                                </span>
                                <span className="text-[10px] tracking-widest">→</span>
                            </Link>
                        </div>

                        <div className="mt-24 md:mt-32 flex items-center justify-center gap-4 text-[10px] uppercase tracking-[0.3em] text-zinc-700">
                            <span>LAND-0001</span>
                            <span>·</span>
                            <span>EXP-0001</span>
                            <span>·</span>
                            <span>NZ-001</span>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}
